import type { SelectedProduct } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { CurrencyField, DateField, DropdownField, TextField } from "./Fields";
import {
  buildGapDefaultValues,
  GAP_FORM_NUMBER_CODES,
  GAP_FORM_NUMBER_NOT_LISTED,
  GAP_FORM_NUMBER_SELECT_AN_OPTION,
  GAP_VENDOR_OPTIONS,
  gapInformationSchema,
} from "./GapInformation.schema";

interface GapInformationProperties {
  readonly gap?: SelectedProduct;
  /** Whether every field below is typeable/selectable — condition TBD; defaults to disabled (today's behavior). */
  readonly editable?: boolean;
}

/**
 * RHF + Zod, same pattern as VehicleServiceContractInformation.tsx — see
 * GapInformation.schema.ts for the validation rules and for what's mocked vs.
 * sourced from the real `SelectedProduct` prop. Unlike every other field
 * here, Addendum State-Prefix/Number are required regardless of `tpapGap`
 * (see the schema's comment) — they render as the same pair in both the
 * non-TPAP and TPAP blocks below, same conceptual value either way.
 *
 * Not registered with `useRegisterSaveableSection`, same reasoning as
 * InsuranceInformation/VehicleServiceContractInformation: `ContractVehicleInformation`
 * already owns the `DashboardSaveV2Request.vehicle` key, and the shared save
 * orchestrator's merge is shallow/last-wins, so a second section writing to
 * `vehicle` would silently clobber one or the other's data.
 */
export function GapInformation({ gap, editable = false }: GapInformationProperties) {
  const { t } = useTranslation();
  const disabled = !editable;
  const form = useForm({
    resolver: zodResolver(gapInformationSchema),
    mode: "onBlur",
    defaultValues: buildGapDefaultValues(gap),
  });
  const { control } = form;
  const tpapGap = useController({ control, name: "tpapGap" });
  const vendorDisplay = useController({ control, name: "vendorDisplay" });
  const tpapVendor = useController({ control, name: "tpapVendor" });
  const addendumStatePrefix = useController({ control, name: "addendumStatePrefix" });
  const addendumNumber = useController({ control, name: "addendumNumber" });
  const sellingPrice = useController({ control, name: "sellingPrice" });
  const termCoverage = useController({ control, name: "termCoverage" });
  const amountFinanced = useController({ control, name: "amountFinanced" });
  const effectiveDate = useController({ control, name: "effectiveDate" });
  const formNumber = useController({ control, name: "formNumber" });

  const tpapVendorOptions = GAP_VENDOR_OPTIONS.map((vendor) => ({ value: vendor, label: vendor }));
  const formNumberOptions = [
    { value: GAP_FORM_NUMBER_SELECT_AN_OPTION, label: t("vehicle.gap.selectAnOption") },
    ...GAP_FORM_NUMBER_CODES.map((code) => ({ value: code, label: code })),
    { value: GAP_FORM_NUMBER_NOT_LISTED, label: t("vehicle.gap.formNotListed") },
  ];

  // Changing the TPAP vendor resets Form Number back to unset (no vendor-scoped
  // form list/correction-letter flag exists here — see the schema's comment).
  function handleTpapVendorChange(value: string) {
    tpapVendor.field.onChange(value);
    formNumber.field.onChange(GAP_FORM_NUMBER_SELECT_AN_OPTION);
    void form.trigger("formNumber");
  }

  // Validates on change rather than blur (like CPO Date/VSC's Form Number) —
  // selecting an option doesn't blur the trigger, so `mode: "onBlur"` alone
  // would never reveal the required error.
  function handleFormNumberChange(value: string) {
    formNumber.field.onChange(value);
    void form.trigger("formNumber");
  }

  const addendumFields = (
    <>
      <TextField
        label={t("vehicle.gap.addendumStatePrefix")}
        value={addendumStatePrefix.field.value}
        onChange={addendumStatePrefix.field.onChange}
        onBlur={addendumStatePrefix.field.onBlur}
        error={addendumStatePrefix.fieldState.error}
        disabled={disabled}
      />
      <TextField
        label={t("vehicle.gap.addendumNumber")}
        value={addendumNumber.field.value}
        onChange={addendumNumber.field.onChange}
        onBlur={addendumNumber.field.onBlur}
        error={addendumNumber.fieldState.error}
        disabled={disabled}
      />
    </>
  );

  return (
    <div>
      <SubsectionHeader>{t("vehicle.gap.heading")}</SubsectionHeader>
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="px-(--spacing-3) py-(--spacing-3) gap-(--spacing-4) grid grid-cols-2">
            {!tpapGap.field.value && (
              <>
                <TextField label={t("vehicle.gap.vendor")} value={vendorDisplay.field.value} disabled />
                {addendumFields}
              </>
            )}
            <CurrencyField
              label={t("vehicle.gap.sellingPrice")}
              value={sellingPrice.field.value}
              onChange={sellingPrice.field.onChange}
              onBlur={sellingPrice.field.onBlur}
              error={sellingPrice.fieldState.error}
              disabled={disabled || !tpapGap.field.value}
            />
            {tpapGap.field.value && (
              <>
                <DropdownField
                  label={t("vehicle.gap.vendor")}
                  value={tpapVendor.field.value}
                  onChange={handleTpapVendorChange}
                  options={tpapVendorOptions}
                  disabled={disabled}
                />
                {addendumFields}
                <TextField
                  label={t("vehicle.gap.termCoverage")}
                  value={termCoverage.field.value}
                  onChange={termCoverage.field.onChange}
                  onBlur={termCoverage.field.onBlur}
                  error={termCoverage.fieldState.error}
                  disabled={disabled}
                />
                <CurrencyField
                  label={t("vehicle.gap.amountFinanced")}
                  value={amountFinanced.field.value}
                  onChange={amountFinanced.field.onChange}
                  onBlur={amountFinanced.field.onBlur}
                  error={amountFinanced.fieldState.error}
                  disabled={disabled}
                />
                <DateField
                  label={t("vehicle.gap.effectiveDate")}
                  value={effectiveDate.field.value}
                  onChange={effectiveDate.field.onChange}
                  onBlur={effectiveDate.field.onBlur}
                  error={effectiveDate.fieldState.error}
                  disabled={disabled}
                />
                <DropdownField
                  label={t("vehicle.gap.formNumber")}
                  value={formNumber.field.value}
                  onChange={handleFormNumberChange}
                  onBlur={formNumber.field.onBlur}
                  error={formNumber.fieldState.error}
                  options={formNumberOptions}
                  disabled={disabled}
                />
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
